import { NotifyHelper } from 'classes/helpers/notify.helper';
import { MAX_SHOTS_USED } from 'classes/plate-canvas';
import { t } from 'i18next';
import { Controller } from 'lib_ts/enums/routing/controllers';
import { ShotsRoutes } from 'lib_ts/enums/routing/routes/shots';
import { IServerResponse } from 'lib_ts/interfaces/common/i-server-response';
import {
  IAggregateCollectionRequest,
  IAggregateCollectionResult,
  IAggregateMachineShotsDict,
  IAggregateMachineShotsRequest,
  IArchiveMachineShotsByDateRequest,
  IArchiveMachineShotsByHashRequest,
  IMachineShot,
  IMatchingShotsDict,
  IMatchingShotsRequest,
} from 'lib_ts/interfaces/training/i-machine-shot';
import { BaseRESTService } from 'services/_base-rest.service';

const MATCHING_SHOTS_LIMIT = MAX_SHOTS_USED + 1;

/** example service for pulling data from server
 * note the singleton definition so we only ever have 1 instance
 */
export class ShotsService extends BaseRESTService {
  private static instance: ShotsService;
  static getInstance(): ShotsService {
    if (!ShotsService.instance) {
      ShotsService.instance = new ShotsService();
    }

    return ShotsService.instance;
  }

  private constructor() {
    super({
      controller: Controller.Shot,
    });
  }

  async updateShot(
    shotID: string,
    payload: Partial<IMachineShot>
  ): Promise<IMachineShot | undefined> {
    return await this.put(
      {
        uri: ShotsRoutes.UpdateShot,
        params: {
          shot_id: shotID,
        } as any,
      },
      payload
    )
      .then((result: IServerResponse) => {
        if (!result.success) {
          NotifyHelper.warning({
            message_md: result.error ?? t('common.request-failed-msg'),
          });
          return undefined;
        }

        return result.data as IMachineShot;
      })
      .catch((e) => {
        console.error(e);
        NotifyHelper.error({
          message_md: t('common.request-failed-msg'),
        });
        return undefined;
      });
  }

  async archiveByMachine(machineID: string): Promise<boolean> {
    return await this.get({
      uri: `${ShotsRoutes.ArchiveByMachine}/${machineID}`,
    })
      .then((result) => !!result && result.success)
      .catch((e) => {
        console.error(e);
        return false;
      });
  }

  async archiveById(shot: IMachineShot): Promise<boolean> {
    return await this.post(
      {
        uri: ShotsRoutes.ArchiveById,
      },
      shot
    )
      .then((result) => !!result && result.success)
      .catch((e) => {
        console.error(e);
        return false;
      });
  }

  async archiveByHashes(
    config: IArchiveMachineShotsByHashRequest
  ): Promise<boolean> {
    return await this.post(
      {
        uri: ShotsRoutes.ArchiveByHashes,
      },
      config
    )
      .then((result) => !!result && result.success)
      .catch((e) => {
        console.error(e);
        return false;
      });
  }

  async archiveByDates(
    config: IArchiveMachineShotsByDateRequest
  ): Promise<boolean> {
    return await this.post(
      {
        uri: ShotsRoutes.ArchiveByDates,
      },
      config
    )
      .then((result) => !!result && result.success)
      .catch((e) => {
        console.error(e);
        return false;
      });
  }

  async archiveByList(listID: string): Promise<boolean> {
    return await this.get({
      uri: `${ShotsRoutes.ArchiveByList}/${listID}`,
    })
      .then((result) => !!result && result.success)
      .catch((e) => {
        console.error(e);
        return false;
      });
  }

  /** uses matching_hash where possible, falling back to ms attributes otherwise */
  async getMatches(
    config: IMatchingShotsRequest,
    limit?: number
  ): Promise<IMatchingShotsDict> {
    return await this.post(
      {
        uri: ShotsRoutes.GetMatches,
        params: {
          limit: limit ?? MATCHING_SHOTS_LIMIT,
        } as any,
      },
      config
    );
  }

  async getCountCollection(
    payload: IAggregateCollectionRequest
  ): Promise<number> {
    return await this.post(
      {
        uri: ShotsRoutes.CountCollection,
      },
      payload
    ).then((result) => (result.data.count as number) ?? 0);
  }

  async getAggCollection(
    payload: IAggregateCollectionRequest
  ): Promise<IAggregateCollectionResult[]> {
    return await this.post(
      {
        uri: ShotsRoutes.AggCollection,
      },
      payload
    );
  }

  async getHashCollection(
    payload: IAggregateCollectionRequest
  ): Promise<IMachineShot[]> {
    if (!payload.matching_hash) {
      return [];
    }

    return await this.post(
      {
        uri: ShotsRoutes.HashCollection,
      },
      payload
    );
  }

  /** uses matching_hash */
  async getAggMatches(
    payload: IAggregateMachineShotsRequest
  ): Promise<IAggregateMachineShotsDict | undefined> {
    if (!payload.machineID) {
      console.warn('Cannot perform getAggregateMatches without a machineID');
      return undefined;
    }

    return await this.post(
      {
        uri: ShotsRoutes.AggMatches,
      },
      payload
    )
      .then((result) => {
        return result;
      })
      .catch((error) => {
        console.error(error);

        return undefined;
      });
  }
}
